import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Spin, Typography } from "antd";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { notification } from "shared/lib/antd-global";
import ROUTES from "shared/routes";
import { HelpContacts } from "widgets/help-contacts";
import { startIdentityVerification } from "../api";
import {
  IdentityVerificationData,
  IdentityVerificationFailure,
} from "../types";
import styles from "./index.module.scss";
import {
  VerifyIdentitiesModal,
  VerifyIdentitiesModalType,
} from "./verify-identities-modal";

type CardAttachButtonProps = {
  verificationData: IdentityVerificationData;
  goNext?: () => void;
  isMain?: boolean;
  /** ИИН из профиля — подставляется в поле, его можно изменить. */
  profileInn?: string;
};

/** Для новых кодов — свой текст, для остальных — сообщение сервера. */
const FAILURE_TRANSLATIONS: Partial<
  Record<IdentityVerificationFailure["code"], string>
> = {
  profile_iin_mismatch: "identity_verification.failure_profile_iin_mismatch",
  iin_input_mismatch: "identity_verification.failure_iin_input_mismatch",
};

export const VerifyIdentitiesBlock = ({
  verificationData,
  goNext,
  isMain,
  profileInn,
}: CardAttachButtonProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const verifyIdentitiesModalRef = useRef<VerifyIdentitiesModalType>(null);
  const [inn, setInn] = useState(profileInn ?? "");

  // Профиль мог загрузиться позже — подставляем ИИН, пока поле не трогали.
  useEffect(() => {
    if (profileInn) {
      setInn((current) => current || profileInn);
    }
  }, [profileInn]);

  const failure = verificationData.failure;
  const failureKey = failure && FAILURE_TRANSLATIONS[failure.code];
  const failureMessage = failureKey ? t(failureKey) : failure?.message;

  const { mutate, isPending, error } = useMutation({
    mutationFn: () =>
      startIdentityVerification(isMain ? "main" : "auth", inn.trim()),
    onSuccess(response) {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ["verification-data"] });
        window.open(response.data.next_action?.data.url, "_blank");
        verifyIdentitiesModalRef.current?.close();
      } else {
        notification.error({
          title: t("identity_verification.link_error"),
        });
      }
    },
    onError(
      error: AxiosError<{ message: string; error: { description: string } }>,
    ) {
      console.log(error?.response?.data?.message);
      notification.error({
        title:
          error?.response?.data?.message ||
          error?.response?.data?.error?.description ||
          t("identity_verification.request_error"),
      });
    },
  });

  const verify = () => {
    verifyIdentitiesModalRef.current?.open();
  };

  if (verificationData.status === "verified") {
    return (
      <div className={styles.block}>
        <Typography.Text style={{ fontSize: 16 }}>
          {t("identity_verification.verified")}
        </Typography.Text>
        {goNext ? (
          <Button type="primary" onClick={goNext} block>
            {t("common.next")}
          </Button>
        ) : (
          <Button type="primary" onClick={() => navigate(ROUTES.MAIN)} block>
            {t("identity_verification.go_to_main")}
          </Button>
        )}
      </div>
    );
  }

  if (verificationData.status === "required" && verificationData.can_retry) {
    return (
      <>
        <Button
          type="primary"
          onClick={verify}
          loading={isPending}
          disabled={isPending}
          block
        >
          {t("identity_verification.verify_button")}
        </Button>

        <VerifyIdentitiesModal
          ref={verifyIdentitiesModalRef}
          error={error}
          isPending={isPending}
          inn={inn}
          setInn={setInn}
          mutate={mutate}
        />
      </>
    );
  }

  if (verificationData.status === "failed" && verificationData.can_retry) {
    return (
      <>
        <div className={styles.block}>
          <Typography.Text style={{ fontSize: 16 }}>
            {t("identity_verification.failed_message")} <br />
            <Typography.Text type="danger">{failureMessage}</Typography.Text>
          </Typography.Text>
          <Typography.Text style={{ fontSize: 16 }}>
            {t("identity_verification.retry_prompt")}
          </Typography.Text>
          <Button
            type="primary"
            onClick={verify}
            loading={isPending}
            disabled={isPending}
            block
          >
            {t("identity_verification.retry")}
          </Button>
        </div>

        <VerifyIdentitiesModal
          ref={verifyIdentitiesModalRef}
          error={error}
          isPending={isPending}
          inn={inn}
          setInn={setInn}
          mutate={mutate}
        />
      </>
    );
  }

  if (verificationData.status === "in_progress") {
    if (verificationData.phase === "awaiting_user") {
      return (
        <div className={styles.block}>
          <Typography.Text style={{ fontSize: 16 }}>
            {t("identity_verification.active_attempt")}
          </Typography.Text>
          <Button
            type="primary"
            onClick={() =>
              window.open(verificationData.next_action?.data.url, "_blank")
            }
            block
          >
            {t("identity_verification.go_to_verification")}
          </Button>
        </div>
      );
    }
    if (
      verificationData.phase === "awaiting_provider" ||
      verificationData.phase === "manual_review"
    ) {
      return (
        <div className={styles.block}>
          <Spin />
          <Typography.Text style={{ fontSize: 16 }}>
            {verificationData.phase === "awaiting_provider"
              ? t("identity_verification.awaiting_provider")
              : t("identity_verification.manual_review")}
          </Typography.Text>
        </div>
      );
    }
    if (verificationData.phase === "unknown") {
      return (
        <div className={styles.block}>
          <Typography.Text style={{ fontSize: 16 }}>
            {t("identity_verification.unknown_phase")}
          </Typography.Text>
        </div>
      );
    }
  }

  return (
    <>
      <div className={styles.block} style={{ marginBottom: 24 }}>
        <Typography.Text style={{ fontSize: 16 }}>
          {failureMessage ? (
            <>
              {t("identity_verification.failed_message")} <br />
              <Typography.Text type="warning">{failureMessage}</Typography.Text>
            </>
          ) : (
            t("identity_verification.error_occurred")
          )}
        </Typography.Text>

        <Typography.Text style={{ fontSize: 16 }}>
          {t("identity_verification.contact_support")}
        </Typography.Text>
      </div>
      <HelpContacts />
    </>
  );
};
